export interface VersionInfo {
  version: string;
  buildNumber: string;
  buildDate: string;
  environment: string;
  minimumRequiredVersion?: string;
  forceUpdate?: boolean;
  updateUrl?: string;
  message?: string;
}

export interface Version {
  major: number;
  minor: number;
  patch: number;
  build?: number;
}

/**
 * Parse version string into Version object
 * @param versionString - Version string (e.g., "1.2.3" or "1.2.3.4")
 * @returns Version object
 */
export function parseVersion(versionString: string): Version {
  const parts = versionString.split('.').map(part => parseInt(part, 10));
  
  if (parts.length < 3) {
    throw new Error(`Invalid version format: ${versionString}`);
  }
  
  return {
    major: parts[0],
    minor: parts[1],
    patch: parts[2],
    build: parts[3] || undefined
  };
}

/**
 * Compare two versions
 * @param version1 - First version
 * @param version2 - Second version
 * @returns -1 if version1 < version2, 0 if equal, 1 if version1 > version2
 */
export function compareVersions(version1: string, version2: string): number {
  const v1 = parseVersion(version1);
  const v2 = parseVersion(version2);
  
  if (v1.major !== v2.major) {
    return v1.major - v2.major;
  }
  
  if (v1.minor !== v2.minor) {
    return v1.minor - v2.minor;
  }
  
  if (v1.patch !== v2.patch) {
    return v1.patch - v2.patch;
  }
  
  // Compare build numbers if both exist
  if (v1.build !== undefined && v2.build !== undefined) {
    return v1.build - v2.build;
  }
  
  // If only one has build number, the one without is considered older
  if (v1.build !== undefined) return 1;
  if (v2.build !== undefined) return -1;
  
  return 0;
}

/**
 * Check if current version is older than required version
 * @param currentVersion - Current app version
 * @param requiredVersion - Minimum required version
 * @returns true if update is required
 */
export function isUpdateRequired(currentVersion: string, requiredVersion: string): boolean {
  return compareVersions(currentVersion, requiredVersion) < 0;
}

/**
 * Check if current version is newer than required version
 * @param currentVersion - Current app version
 * @param requiredVersion - Required version
 * @returns true if current version is newer
 */
export function isVersionNewer(currentVersion: string, requiredVersion: string): boolean {
  return compareVersions(currentVersion, requiredVersion) > 0;
}

/**
 * Get current app version from package.json
 * @returns Current version string
 */
export function getCurrentVersion(): string {
  // Try to get from environment variable first (set during build)
  if (process.env.REACT_APP_VERSION) {
    return process.env.REACT_APP_VERSION;
  }
  
  // Fallback to hardcoded version (should match package.json)
  // This ensures the version is always available even in development
  return '2.2.10';
}

/**
 * Get current build number
 * @returns Build number string
 */
export function getCurrentBuildNumber(): string {
  return process.env.REACT_APP_BUILD_NUMBER || '1';
}

/**
 * Get current environment
 * @returns Environment string
 */
export function getCurrentEnvironment(): string {
  return process.env.REACT_APP_ENVIRONMENT || 'development';
}

/**
 * Format version for display
 * @param version - Version string
 * @returns Formatted version string
 */
export function formatVersion(version: string): string {
  const parsed = parseVersion(version);
  return `v${parsed.major}.${parsed.minor}.${parsed.patch}`;
}

/**
 * Get version info for current app
 * @returns VersionInfo object
 */
export function getCurrentVersionInfo(): VersionInfo {
  return {
    version: getCurrentVersion(),
    buildNumber: getCurrentBuildNumber(),
    buildDate: new Date().toISOString(),
    environment: getCurrentEnvironment()
  };
} 